<script setup lang="ts">
import type { ListingTrackingContext } from '~/composables/useListingEventTracking';
import type { Property } from '~/core/entities/property';
import type { PaginatedPropertyFilters } from '~/core/entities/property/filters';

import { useEvents } from '~/composables/events/useEvents';
import { buildCarouselTrackingContext } from '~/composables/useListingEventTracking';
import {
	CondoTypeEnum,
	condoTypesTranslations
} from '~/core/entities/CRMCondoEnums';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { buildAddressSlug, CACHE_TIME } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { CONDO_DETAILS } from '~~/utils/symbols';

import { condoTypeByPropertyType } from '..';

const condominium = inject(CONDO_DETAILS);
const sectionName = 'properties around';
const { trackCtaViewMoreClicked } = useEvents();

const key = computed(() => {
	const normalizedPluscodeGroup = normalizeString(
		condominium?.value?.condo.pluscodeGroup
	);

	return `condominium:surrounding-properties-pluscode-group-${normalizedPluscodeGroup}`;
});

const condoType = computed(() => {
	return (
		condominium?.value?.condo.condoType.identifier || CondoTypeEnum.VERTICAL
	);
});

const query = computed<
	Pick<PaginatedPropertyFilters, 'propertyTypes' | 'pluscodesGroup'>
>(() => ({
	pluscodesGroup: [condominium?.value?.condo.pluscodeGroup || ''],
	propertyTypes: condoTypeByPropertyType[condoType.value]
}));

const defaultSurroundingProperties = { payload: [] };
const { user } = useUserSession();

const { data: properties, status: status } = useAsyncData(
	key.value,
	async () => {
		if (!condominium?.value?.condo.pluscodeGroup)
			return defaultSurroundingProperties;

		try {
			const [result, error] = await nuxtPropertiesRepository.list({
				page: 1,
				perPage: 12,
				...query.value,
				markUserWishlistsId: user.value?.id
			});

			if (error) {
				return defaultSurroundingProperties;
			}

			return {
				payload: result.properties
			};
		} catch {
			return defaultSurroundingProperties;
		}
	},
	{
		default: () => defaultSurroundingProperties,
		lazy: true,
		...getCacheAsyncDataConfig<{ payload: Property[] }>(CACHE_TIME),
		watch: [() => condominium?.value?.condo]
	}
);

const isLoadingProperties = computed(() => {
	return status.value === 'pending';
});

const title = computed(() => {
	const type = condoTypesTranslations[condoType.value];

	return `${type} nos arredores do ${condominium?.value?.condo.name}`;
});

const seeAllHref = computed(() => {
	const condo = condominium?.value?.condo;
	if (!condo) return '/imoveis';

	const addressSlug = buildAddressSlug({
		address: condo.address,
		number: condo.number || undefined,
		region: condo.region,
		city: condo.city,
		state: condo.state
	});

	return PropertyFiltersPresenter.getPathFromFilters(
		{
			addressSlug,
			poiLocationLat: condo.lat,
			poiLocationLng: condo.lng
		},
		{},
		{
			page: 'imoveis'
		}
	);
});

const isEmpty = computed(() => {
	return properties.value.payload.length === 0;
});

const trackingContext = computed<ListingTrackingContext>(() =>
	buildCarouselTrackingContext({
		placement: 'condominio_imoveis_arredores',
		properties: properties.value.payload
	})
);

function handleViewMoreClicked() {
	trackCtaViewMoreClicked({
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.DESTINATION_URL]: seeAllHref.value,
		[MixpanelProps.CTA_TEXT]: 'Ver mais',
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Condomínio'
	});
}
</script>

<template>
	<StandardSlider
		v-if="!isEmpty"
		:items="properties.payload as Property[]"
		:view-all-href="seeAllHref"
		:title="title"
		:is-loading="isLoadingProperties"
		:section-name="sectionName"
		button-variant="secondary"
		title-as="h3"
		:tracking-context="trackingContext"
		@cta-view-more-clicked="handleViewMoreClicked"
	/>
</template>
